import * as React from "react";
import {Component } from "react";
import {Button, Label, Table} from "semantic-ui-react";

import "./journal.scss";

import {AppState} from "../../components/app/app-mobx";
import {Auftragsverwaltung} from "../../stores/auftragsverwaltung-mobx";
import {Journal, OperationType} from "../../models/journal";
import {NavigationConfiguration} from "../../config/navigation-configuration";

import {observer} from "mobx-react";
import {autorun} from "mobx";

import _ from "lodash";
import {Difference} from "../../models/difference";
import moment from "moment/moment";
import {JournalizedType} from "../../stores/journalized-type";
import {Project} from "../../models/project";
import {LayerObjectType} from "../../stores/layer-object-type";
import {Contractor} from "../../models/contractor";


export interface JournalListProps {
    view: __esri.MapView | __esri.SceneView | undefined;
    appStore: AppState;
    auftragsverwaltung: Auftragsverwaltung;
    goToProject: (project: Project) => void;
    goToContractor: (contractor: Contractor) => void;
}

export interface SortAction {
    type: string;
    column: string;
}

@observer
export class JournalList extends Component<JournalListProps, any> {
    autorunDisposers: any[] = [];
    constructor(props: JournalListProps) {
        super(props);
        this.state = {
            isLoading: true,
            error: null,
            column: null,
            journals: null,
            direction: null
        };
        this.sort = this.sort.bind(this);
    }

    componentDidMount() {
        this.autorunDisposers.push(autorun((reaction: any) => {
            const {appStore, auftragsverwaltung} = this.props;

            if (!auftragsverwaltung.apiLoaded) {
                return;
            }

            reaction.dispose();

            auftragsverwaltung.setShowMap(false);

            // note GK: load data with Promises, sort and keep in component state
            auftragsverwaltung.JournalStore.loadItems().then((journals: Journal[])=> {
                this.setState({
                    journals: _.orderBy(journals, ["createdAt"], ["desc"]),
                });
            });

            const {topic} = appStore;

            const newRoute = topic.routes[0];
            const breadcrumbs = new NavigationConfiguration();
            breadcrumbs.items = [
                { href: newRoute, title: topic.label }
            ];

            appStore.setPageTitle(topic.label);
            appStore.setTopContent(null);
            appStore.setBottomContent(null);
            appStore.setAsideContent(null);
            appStore.setBreadcrumbs(breadcrumbs);
        }));
    }

    componentWillUnmount() {
        this.autorunDisposers.forEach((dispose: any) => {
            dispose();
        });
        this.autorunDisposers.length = 0;
    }

    render(): React.ReactElement | null {
        const journals = this.state.journals;

        if (!journals) {
            return null;
        }

        const tableRows: React.ReactElement[] = [];
        journals.forEach((journal: Journal) => {
            tableRows.push(
                <Table.Row key={journal.objectId}>
                    <Table.Cell width={2}
                        positive={journal.operationType === "add"}
                        negative={journal.operationType === "delete"}>
                        {this.linkTo(journal.journalizedType, journal.journalizedId)}
                    </Table.Cell>
                    <Table.Cell width={2}
                        positive={journal.operationType === "add"}
                        negative={journal.operationType === "delete"}>
                        {journal.notes}
                    </Table.Cell>
                    <Table.Cell width={9}
                        positive={journal.operationType === "add"}
                        negative={journal.operationType === "delete"}>
                        {
                            this.parsedMessages(
                                journal.journalizedType,
                                journal.operationType,
                                journal.diffs
                            )?.join(" | ")
                        }
                    </Table.Cell>
                    <Table.Cell width={3}
                        positive={journal.operationType === "add"}
                        negative={journal.operationType === "delete"}>
                        {journal.creator}
                    </Table.Cell>
                    <Table.Cell width={3}
                        positive={journal.operationType === "add"}
                        negative={journal.operationType === "delete"}>
                        {moment(journal.createdAt).format("DD.MM.YYYY, HH:mm:ss")}
                    </Table.Cell>
                </Table.Row>
            );
        });
        const { column, direction } = this.state;

        return (
            <Table sortable celled compact size="small" className="journal">
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell
                            sorted={column === "journalizedId" ? direction : null}
                            onClick={() => this.sort({ type: "CHANGE_SORT", column: "journalizedId" })}
                        >Item</Table.HeaderCell>
                        <Table.HeaderCell
                            sorted={column === "note" ? direction : null}
                            onClick={() => this.sort({ type: "CHANGE_SORT", column: "note" })}
                        >Notiz</Table.HeaderCell>
                        <Table.HeaderCell
                            sorted={column === "data" ? direction : null}
                            onClick={() => this.sort({ type: "CHANGE_SORT", column: "data" })}
                        >Daten</Table.HeaderCell>
                        <Table.HeaderCell
                            sorted={column === "creator" ? direction : null}
                            onClick={() => this.sort({ type: "CHANGE_SORT", column: "creator" })}
                        >Erstellt von</Table.HeaderCell>
                        <Table.HeaderCell
                            sorted={column === "createdAt" ? direction : null}
                            onClick={() => this.sort({ type: "CHANGE_SORT", column: "createdAt" })}
                        >Erstellt am</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {tableRows}
                </Table.Body>
            </Table>
        );
    }

    sort(action: SortAction): void {
        switch (action.type) {
        case "CHANGE_SORT":
            if (this.state.column === action.column) {
                this.setState({
                    journals: this.state.journals.reverse(),
                    direction: this.state.direction === "ascending" ? "descending" : "ascending"
                });
            } else {
                this.setState({
                    column: action.column,
                    journals: _.sortBy(this.state.journals, [action.column], ["asc"]),
                    direction: "ascending"
                });
            }
            return;
        default:
            throw new Error();
        }
    }

    parsedMessages(type: JournalizedType, operationType: OperationType, diff: Difference[]): string[] | null {
        const {auftragsverwaltung} = this.props;

        if (!diff) {
            return [];
        }

        // map journalizedType to LayerObjectType
        const layerObjectType = this.mapJournalizedTypeToLayerType(type);

        switch (operationType) {
        case "add":
            // there are two diffs holding ID and name of new project
            return diff.map((d: Difference) => {
                const label = auftragsverwaltung.getFieldAlias(layerObjectType, d.p);
                return `${label} "${d.n}"`;
            });
        case "update":
            return diff.map((d: Difference) => {
                const label = auftragsverwaltung.getFieldAlias(layerObjectType, d.p);
                return `${label} geändert von "${d.o}" zu "${d.n}"`;
            });
        case "delete":
            // there are two diffs holding ID and name of deleted project
            return diff.map((d: Difference) => {
                const label = auftragsverwaltung.getFieldAlias(layerObjectType, d.p);
                return `${label} "${d.o}"`;
            });

        default:
            return null;
        }
    }

    private linkTo(itemType: JournalizedType, itemId: string): React.ReactElement | null {
        const {auftragsverwaltung, goToProject, goToContractor} = this.props;

        if (!itemType || !itemId) {
            return null;
        }

        const toProject = ["project", "process-source", "process-log", "project-process", "project-perimeter",
            "partial-order", "project-municipality", "municipality", "parcel", "document"];

        if (toProject.includes(itemType)) {
            // link to project
            const projectId = parseInt(itemId, 10);
            return (
                <Button className={"go-to-project geo-link-button"}
                    onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        auftragsverwaltung.ProjectStore?.loadItem(projectId).then((p: Project) => {
                            if (goToProject) {
                                goToProject(p);
                            }
                        });
                    }}>
                    Projekt {itemId}
                </Button>
            );
        }
        if (itemType === "contractor") {
            // link to contractor
            const contractorId = parseInt(itemId, 10);
            return (
                <Button className={"go-to-contractor geo-link-button"}
                    onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        auftragsverwaltung.ContractorStore?.loadItem(contractorId).then((c: Contractor) => {
                            if (goToContractor) {
                                goToContractor(c);
                            }
                        });
                    }}>
                    Auftragnehmer {itemId}
                </Button>
            );
        }
        return <Label color="orange">Unbekannter Item Typ</Label>;
    }

    private mapJournalizedTypeToLayerType(journalizedType: JournalizedType): LayerObjectType {
        if (journalizedType.includes("partial-order")) {
            // journalized type 'partial-order' does not match LayerObjectType
            return "partial-order-gk";
        }
        return journalizedType as LayerObjectType;
    }

}
